import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import { useMemo } from 'react';
import { useDevice, useDeviceLocationHistory } from '../api/hooks';
import type { GeoHistoryPoint } from '../api/hooks';
import { fmtAgo } from './format';

const OSM_TILES = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const OSM_ATTR =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

/**
 * Compact map card classifying a session into "we know where the
 * device was" vs "we don't know" using two signals:
 *
 *   1. `geo_history` rows — written ONLY on real movement (the
 *      resolver's "good" path with a new lat/lon). Sparse: a stationary
 *      device might accumulate one row total, ever.
 *   2. `shadow.geo` — current verification state. `consecutive_failures
 *      > 0` means the cloud is in a failure streak right now.
 *
 * Five states, evaluated top-down (first match wins):
 *
 *   - **No location**: empty geo_history AND no shadow last_verified_*
 *   - **Verified during session**: a geo_history row sits inside
 *     `[session_start, session_end]` — direct proof.
 *   - **Moved after session**: a geo_history row exists *after*
 *     `session_end`. The pre-session pin shows the position before the
 *     session, but the device moved later; uncertain whether the demo
 *     was at the pin or somewhere along the way.
 *   - **Cloud cannot currently verify**: shadow says
 *     `consecutive_failures > 0`. The cloud is failing right now; if
 *     those failures started before session_end, the session-time
 *     position is uncertain too.
 *   - **Stable — no detected movement**: cloud currently healthy, no
 *     movement detected before or after the session. Best we can claim
 *     without an attempt-history record.
 *
 * The "stable" claim has one honest limit, called out in the plan
 * (DE-162 follow-up): a device that temporarily moved to a venue
 * Lambda couldn't resolve and then returned cannot be distinguished
 * from one that never moved. Solving that requires logging every
 * Lambda attempt (not just movements).
 */

type CardState =
  | { kind: 'none' }
  | {
      kind: 'verifiedDuring';
      point: GeoHistoryPoint;
    }
  | {
      kind: 'movedAfter';
      point: GeoHistoryPoint;       // best pre-session reference
      nextMove: GeoHistoryPoint;    // first post-session movement
    }
  | {
      kind: 'cloudLost';
      point: GeoHistoryPoint;
      consecutiveFailures: number;
      lastAttemptStatus: string | null;
    }
  | {
      kind: 'stable';
      point: GeoHistoryPoint;
    };

export function SessionLocationCard({
  thing,
  startISO,
  endISO,
}: {
  thing: string;
  startISO: string;
  endISO: string;
}) {
  // Widen the geo_history window to `now`. We need to see post-session
  // rows to classify "moved after session"; pre-session rows for the
  // fallback pin. Volume is fine — geo_history is sparse.
  const history = useDeviceLocationHistory(
    thing,
    '1970-01-01T00:00:00Z',
  );
  // Shadow gives us consecutive_failures / last_attempt_status — the
  // "cloud currently lost" signal.
  const device = useDevice(thing);

  const startMs = new Date(startISO).getTime();
  const endMs = new Date(endISO).getTime();

  const state: CardState = useMemo(() => {
    const raw = history.data?.points ?? [];
    const geo = device.data?.device?.geo ?? null;

    // Classify each row: in-window, after-session, or pre-session.
    let inWindow: GeoHistoryPoint | null = null;
    let earliestAfter: GeoHistoryPoint | null = null;
    let latestBefore: GeoHistoryPoint | null = null;
    for (const p of raw) {
      const ts = new Date(p.ts).getTime();
      if (ts >= startMs && ts <= endMs) {
        if (!inWindow) inWindow = p;
      } else if (ts > endMs) {
        // backend is newest-first; the LAST iteration of after-session
        // candidates is the EARLIEST one (smallest ts > endMs).
        earliestAfter = p;
      } else {
        // ts < startMs — pre-session
        if (!latestBefore) latestBefore = p;
      }
    }

    // Direct proof: a fix inside the session window.
    if (inWindow) {
      return { kind: 'verifiedDuring', point: inWindow };
    }

    // Definite post-session movement — even if the cloud is now lost,
    // this is the stronger signal. The "best reference pin" is the
    // latest pre-session row if any, else the earliest-after fix
    // (which IS where the device went, just after the session).
    if (earliestAfter) {
      return {
        kind: 'movedAfter',
        point: latestBefore ?? earliestAfter,
        nextMove: earliestAfter,
      };
    }

    // From here on: no in-window, no after-session rows. The pin
    // candidate is the latest pre-session row OR a synthesised point
    // from the shadow's last_verified_* (covers devices whose only
    // recorded position is in the shadow, not yet in geo_history).
    let pin: GeoHistoryPoint | null = latestBefore;
    if (!pin && geo) {
      const lv_lat = geo.last_verified_lat;
      const lv_lon = geo.last_verified_lon;
      const lv_at = geo.last_verified_at;
      if (
        typeof lv_lat === 'number' && Number.isFinite(lv_lat) &&
        typeof lv_lon === 'number' && Number.isFinite(lv_lon)
      ) {
        pin = {
          ts: lv_at != null ? new Date(Number(lv_at) * 1000).toISOString() : '',
          lat: lv_lat,
          lon: lv_lon,
          accuracy_m: typeof geo.last_verified_accuracy === 'number'
            ? geo.last_verified_accuracy
            : 0,
          source: geo.last_verified_source ?? null,
          // DE-170 — the shadow mirrors address/place_name but not the
          // place_type/candidates, which only live on geo_history rows.
          address: geo.last_verified_address ?? null,
          place_name: geo.last_verified_place_name ?? null,
          place_type: null,
          place_candidates: null,
        };
      }
    }

    if (!pin) {
      return { kind: 'none' };
    }

    // Cloud currently in a failure streak → uncertain about the
    // session period (the failures may have started before session_end).
    const failures = geo?.consecutive_failures ?? 0;
    if (failures > 0) {
      return {
        kind: 'cloudLost',
        point: pin,
        consecutiveFailures: failures,
        lastAttemptStatus: geo?.last_attempt_status ?? null,
      };
    }

    // Otherwise: no movement detected, cloud healthy. Best-claim stable.
    return { kind: 'stable', point: pin };
  }, [history.data, device.data, startMs, endMs]);

  if (history.isLoading || device.isLoading) {
    return (
      <section className="card">
        <h2 className="font-medium mb-2">Location</h2>
        <div className="h-[200px] bg-soft rounded animate-pulse" />
      </section>
    );
  }

  if (state.kind === 'none') {
    return (
      <section className="card">
        <h2 className="font-medium mb-2">Location</h2>
        <p className="text-sm text-muted">
          No location recorded for this session.
        </p>
      </section>
    );
  }

  const { point } = state;
  const accuracy = point.accuracy_m ?? 0;
  const stamp = point.ts ? fmtAgo(point.ts) : null;
  const stampAbs = point.ts
    ? new Date(point.ts).toLocaleString([], {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;
  const isUncertain = state.kind === 'movedAfter' || state.kind === 'cloudLost';
  const ringColor = isUncertain ? '#94a3b8' : '#2563eb';
  const ringDashArray = isUncertain ? '4 4' : undefined;

  let badge: { text: string; cls: string };
  let warning: string | null = null;
  let popupExtra: string | null = null;
  if (state.kind === 'verifiedDuring') {
    badge = { text: 'verified during session', cls: 'text-accent' };
  } else if (state.kind === 'stable') {
    badge = { text: 'no movement detected', cls: 'text-muted' };
  } else if (state.kind === 'movedAfter') {
    badge = { text: 'device moved after session', cls: 'text-warn italic' };
    const nextStampAbs = new Date(state.nextMove.ts).toLocaleString([], {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
    warning =
      `Device moved after this session (next fix recorded ${nextStampAbs}). The pin shows the position before the session; the demo may have been somewhere different.`;
    popupExtra = 'moved after — pin is pre-session position';
  } else {
    // cloudLost
    badge = { text: 'cloud cannot currently verify', cls: 'text-warn italic' };
    const failPart =
      `${state.consecutiveFailures} inconclusive scan${state.consecutiveFailures === 1 ? '' : 's'}` +
      (state.lastAttemptStatus ? ` (most recent: ${state.lastAttemptStatus})` : '');
    warning =
      `The cloud hasn't been able to verify this device's location since ${stampAbs ?? 'the last fix'} — ${failPart}. The pin shows the last known position; the device may or may not still be there.`;
    popupExtra = 'cloud currently cannot verify position';
  }

  return (
    <section className="card">
      <div className="flex items-baseline justify-between mb-2 gap-3">
        <h2 className="font-medium">Location</h2>
        <span className={`text-[10px] ${badge.cls}`}>{badge.text}</span>
      </div>
      {state.kind === 'stable' && stampAbs && (
        <p className="text-[11px] text-muted mb-2">
          Location stable — no movement detected since the last verified fix at{' '}
          {stampAbs}. The pin shows that position.
        </p>
      )}
      {warning && (
        <p className="text-[11px] text-warn mb-2">{warning}</p>
      )}
      <div className="rounded-md overflow-hidden border border-border">
        <MapContainer
          key={`${point.lat.toFixed(5)},${point.lon.toFixed(5)}`}
          center={[point.lat, point.lon]}
          zoom={15}
          scrollWheelZoom={false}
          style={{ height: 240, width: '100%' }}
        >
          <TileLayer url={OSM_TILES} attribution={OSM_ATTR} />
          <Marker
            position={[point.lat, point.lon]}
            opacity={isUncertain ? 0.5 : 1}
          >
            <Popup>
              <div className="text-xs">
                <div className="font-mono">{thing}</div>
                <div>
                  lat {point.lat.toFixed(5)}, lon {point.lon.toFixed(5)}
                </div>
                <div>accuracy {accuracy ? `${Math.round(accuracy)} m` : '—'}</div>
                {point.source && <div>source {point.source}</div>}
                {stamp && (
                  <div>
                    {state.kind === 'verifiedDuring'
                      ? 'fix'
                      : 'last verified'}{' '}
                    {stamp}
                  </div>
                )}
                {popupExtra && (
                  <div className="text-warn">{popupExtra}</div>
                )}
              </div>
            </Popup>
          </Marker>
          {accuracy > 0 && (
            <Circle
              center={[point.lat, point.lon]}
              radius={accuracy}
              pathOptions={{
                color: ringColor,
                weight: 1,
                fillOpacity: isUncertain ? 0.04 : 0.08,
                dashArray: ringDashArray,
              }}
            />
          )}
        </MapContainer>
      </div>
      <div className="mt-2 text-xs text-muted flex flex-wrap gap-3">
        <span className="tabular-nums">
          {point.lat.toFixed(5)}, {point.lon.toFixed(5)}
        </span>
        <span>±{accuracy ? Math.round(accuracy) : '?'} m</span>
        {point.source && <span>{point.source}</span>}
        {stamp && (
          <span>
            {state.kind === 'verifiedDuring' ? stamp : `last verified ${stamp}`}
          </span>
        )}
      </div>
    </section>
  );
}
